import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend,
  LineChart, Line
} from 'recharts';
import { FiFilter, FiDownload, FiCalendar, FiBarChart2, FiFileText, FiPieChart, FiActivity } from 'react-icons/fi';

/* ══ CONQUER Cyan Theme ══ */
const THEME = {
  primary: '#00B4D8',
  secondary: '#48CAE4',
  accent: '#90E0EF',
  success: '#059669',
  warning: '#D97706',
  danger: '#DC2626',
  dark: '#0A1628',
  slate: '#0096C7',
  muted: '#48CAE4',
};

/* ── Prism Tooltip ── */
const PrismTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'linear-gradient(145deg, rgba(255,255,255,0.98), rgba(245,243,255,0.95))',
      backdropFilter: 'blur(12px)', border: '2px solid #E0E7FF',
      borderRadius: 14, padding: '14px 18px', boxShadow: '0 12px 40px rgba(124,58,237,0.12)',
    }}>
      <p style={{ color: THEME.dark, fontSize: 13, fontWeight: 700, marginBottom: 8 }}>{label}</p>
      {payload.map((p, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <span style={{ width: 10, height: 10, borderRadius: 3, background: p.color || p.fill }} />
          <span style={{ color: THEME.muted, fontSize: 12 }}>{p.name}:</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: THEME.dark }}>{p.value?.toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
};

/* ── Panel Header ── */
const SectionHeader = ({ icon, title, subtitle }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
    <div style={{
      width: 40, height: 40, borderRadius: 10,
      background: `linear-gradient(135deg, ${THEME.primary}12, ${THEME.accent}08)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center', color: THEME.primary,
    }}>
      {icon}
    </div>
    <div>
      <h3 style={{ fontSize: 16, fontWeight: 700, color: THEME.dark, margin: 0 }}>{title}</h3>
      {subtitle && <p style={{ fontSize: 12, color: THEME.muted, margin: 0 }}>{subtitle}</p>}
    </div>
  </div>
);

/* ── Summary Card ── */
const SummaryCard = ({ label, value, color, icon }) => (
  <div style={{
    background: `linear-gradient(145deg, ${color}08, ${color}04)`,
    borderRadius: 14, padding: 18, border: `1px solid ${color}20`,
    display: 'flex', alignItems: 'center', gap: 14,
  }}>
    <div style={{
      width: 44, height: 44, borderRadius: 12, background: `${color}15`,
      display: 'flex', alignItems: 'center', justifyContent: 'center', color,
    }}>
      {icon}
    </div>
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: THEME.muted, textTransform: 'uppercase', letterSpacing: 0.8 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color: THEME.dark }}>{value}</div>
    </div>
  </div>
);

export default function AdminReports() {
  const [report, setReport] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [filters, setFilters] = useState({ startDate: '', endDate: '', campaignId: '', status: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/admin/campaigns?limit=100').then(({ data }) => setCampaigns(data.campaigns || [])).catch(() => {});
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.campaignId) params.campaignId = filters.campaignId;
      if (filters.status) params.status = filters.status;
      const { data } = await api.get('/admin/reports', { params });
      setReport(data);
    } catch { toast.error('Failed to generate report'); }
    finally { setLoading(false); }
  };

  const summary = report?.summary || {};
  const totalSent = summary.totalSent || 0;
  const totalFailed = summary.totalFailed || 0;
  const total = summary.total || 0;
  const successRate = summary.successRate || 0;

  const barData = report ? [
    { name: 'Delivered', value: totalSent, fill: THEME.success },
    { name: 'Failed', value: totalFailed, fill: THEME.danger },
  ] : [];

  const pieData = report ? [
    { name: 'Delivered', value: totalSent, color: THEME.success },
    { name: 'Failed', value: totalFailed, color: THEME.danger },
  ].filter(d => d.value > 0) : [];
  if (report && pieData.length === 0) pieData.push({ name: 'No Data', value: 1, color: '#E0E7FF' });

  const exportCSV = () => {
    if (!report) return;
    const logs = report.logs || [];
    const rows = [['Time', 'Campaign', 'Status', 'Phone']];
    logs.forEach(l => rows.push([new Date(l.createdAt).toLocaleString(), l.campaignId?.name || '', l.status || '', l.phone || '']));
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `report_${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success('Report exported!');
  };

  return (
    <div style={{ padding: '0 4px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 800, color: THEME.dark, margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
            <FiFileText size={28} style={{ color: THEME.primary }} />
            Intelligence Reports
          </h1>
          <p style={{ fontSize: 14, color: THEME.muted, marginTop: 6 }}>
            Generate detailed campaign analytics
          </p>
        </div>
      </div>

      {/* Filters Panel */}
      <div style={{
        background: '#fff', borderRadius: 18, padding: 24, marginBottom: 28,
        border: `1px solid ${THEME.accent}30`, boxShadow: '0 4px 24px rgba(124,58,237,0.08)',
      }}>
        <SectionHeader icon={<FiFilter size={18} />} title="Filters" subtitle="Define your report parameters" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16, alignItems: 'end' }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: THEME.muted, display: 'block', marginBottom: 6 }}>
              <FiCalendar size={12} /> Start Date
            </label>
            <input
              type="date" value={filters.startDate}
              onChange={e => setFilters({ ...filters, startDate: e.target.value })}
              style={{
                width: '100%', padding: '12px 14px', borderRadius: 10, border: '2px solid #E0E7FF',
                fontSize: 13, color: THEME.dark, background: '#FAFAFF',
              }}
            />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: THEME.muted, display: 'block', marginBottom: 6 }}>
              <FiCalendar size={12} /> End Date
            </label>
            <input
              type="date" value={filters.endDate}
              onChange={e => setFilters({ ...filters, endDate: e.target.value })}
              style={{
                width: '100%', padding: '12px 14px', borderRadius: 10, border: '2px solid #E0E7FF',
                fontSize: 13, color: THEME.dark, background: '#FAFAFF',
              }}
            />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: THEME.muted, display: 'block', marginBottom: 6 }}>Campaign</label>
            <select
              value={filters.campaignId}
              onChange={e => setFilters({ ...filters, campaignId: e.target.value })}
              style={{
                width: '100%', padding: '12px 14px', borderRadius: 10, border: '2px solid #E0E7FF',
                fontSize: 13, color: THEME.dark, background: '#FAFAFF', cursor: 'pointer',
              }}
            >
              <option value="">All Campaigns</option>
              {campaigns.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: THEME.muted, display: 'block', marginBottom: 6 }}>Status</label>
            <select
              value={filters.status}
              onChange={e => setFilters({ ...filters, status: e.target.value })}
              style={{
                width: '100%', padding: '12px 14px', borderRadius: 10, border: '2px solid #E0E7FF',
                fontSize: 13, color: THEME.dark, background: '#FAFAFF', cursor: 'pointer',
              }}
            >
              <option value="">All Status</option>
              <option value="sent">Delivered</option>
              <option value="failed">Failed</option>
              <option value="pending">Pending</option>
            </select>
          </div>
          <button
            onClick={fetchReport}
            disabled={loading}
            style={{
              padding: '12px 24px', borderRadius: 12,
              background: loading ? '#E0E7FF' : `linear-gradient(135deg, ${THEME.primary}, ${THEME.secondary})`,
              color: '#fff', fontSize: 14, fontWeight: 700, border: 'none', cursor: loading ? 'wait' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              boxShadow: `0 6px 20px ${THEME.primary}30`,
            }}
          >
            <FiBarChart2 size={18} />
            {loading ? 'Generating...' : 'Generate Report'}
          </button>
        </div>
      </div>

      {/* Report Results */}
      {report ? (
        <>
          {/* Summary Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 18, marginBottom: 28 }}>
            <SummaryCard label="Total Messages" value={total.toLocaleString()} color={THEME.primary} icon={<FiActivity size={22} />} />
            <SummaryCard label="Delivered" value={totalSent.toLocaleString()} color={THEME.success} icon={<FiBarChart2 size={22} />} />
            <SummaryCard label="Failed" value={totalFailed.toLocaleString()} color={THEME.danger} icon={<FiFilter size={22} />} />
            <SummaryCard label="Success Rate" value={`${successRate}%`} color={successRate >= 80 ? THEME.success : THEME.warning} icon={<FiPieChart size={22} />} />
          </div>

          {/* Charts Row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 24, marginBottom: 28 }}>
            {/* Bar Chart */}
            <div style={{ background: '#fff', borderRadius: 18, padding: 24, border: `1px solid ${THEME.accent}30`, boxShadow: '0 4px 24px rgba(124,58,237,0.08)' }}>
              <SectionHeader icon={<FiBarChart2 size={18} />} title="Delivery Analysis" subtitle="Message status breakdown" />
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={barData} margin={{ top: 10, right: 20, bottom: 0, left: -10 }}>
                  <CartesianGrid strokeDasharray="4 4" stroke="#E0E7FF" vertical={false} />
                  <XAxis dataKey="name" tick={{ fill: THEME.muted, fontSize: 12, fontWeight: 600 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: THEME.muted, fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip content={<PrismTooltip />} />
                  <Bar dataKey="value" radius={[10, 10, 0, 0]} barSize={60}>
                    {barData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Pie Chart */}
            <div style={{ background: '#fff', borderRadius: 18, padding: 24, border: `1px solid ${THEME.accent}30`, boxShadow: '0 4px 24px rgba(124,58,237,0.08)' }}>
              <SectionHeader icon={<FiPieChart size={18} />} title="Distribution" subtitle="Delivery ratio" />
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={4} dataKey="value" stroke="none" cornerRadius={6}>
                    {pieData.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                  <Tooltip content={<PrismTooltip />} />
                  <Legend formatter={(v) => <span style={{ color: THEME.dark, fontWeight: 600 }}>{v}</span>} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Export Button */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 28 }}>
            <button
              onClick={exportCSV}
              style={{
                padding: '12px 24px', borderRadius: 12,
                background: `linear-gradient(135deg, ${THEME.success}, #10B981)`,
                color: '#fff', fontSize: 14, fontWeight: 700, border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 8,
                boxShadow: `0 6px 20px ${THEME.success}30`,
              }}
            >
              <FiDownload size={18} />
              Export as CSV
            </button>
          </div>

          {/* Logs Table */}
          {(report.logs?.length > 0) && (
            <div style={{ background: '#fff', borderRadius: 18, padding: 24, border: `1px solid ${THEME.accent}30`, boxShadow: '0 4px 24px rgba(124,58,237,0.08)' }}>
              <SectionHeader icon={<FiFileText size={18} />} title="Delivery Log" subtitle={`${report.logs.length} records`} />
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #E0E7FF' }}>
                      <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: THEME.dark, textTransform: 'uppercase' }}>Time</th>
                      <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: THEME.dark, textTransform: 'uppercase' }}>Campaign</th>
                      <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: THEME.dark, textTransform: 'uppercase' }}>Phone</th>
                      <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: THEME.dark, textTransform: 'uppercase' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.logs.slice(0, 20).map((log, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #F0EEFF' }}>
                        <td style={{ padding: '12px 16px', fontSize: 13, color: THEME.muted }}>{new Date(log.createdAt).toLocaleString()}</td>
                        <td style={{ padding: '12px 16px', fontSize: 13, color: THEME.dark, fontWeight: 600 }}>{log.campaignId?.name || '-'}</td>
                        <td style={{ padding: '12px 16px', fontSize: 13, color: THEME.slate }}>{log.phone || '-'}</td>
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{
                            padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                            background: log.status === 'sent' ? `${THEME.success}15` : `${THEME.danger}15`,
                            color: log.status === 'sent' ? THEME.success : THEME.danger,
                          }}>
                            {log.status === 'sent' ? 'DELIVERED' : 'FAILED'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      ) : (
        /* Empty State */
        <div style={{
          background: '#fff', borderRadius: 18, padding: 60, textAlign: 'center',
          border: `1px solid ${THEME.accent}30`, boxShadow: '0 4px 24px rgba(124,58,237,0.08)',
        }}>
          <div style={{ fontSize: 64, marginBottom: 20 }}>📊</div>
          <h3 style={{ fontSize: 20, fontWeight: 700, color: THEME.dark, margin: 0 }}>Generate Your Report</h3>
          <p style={{ fontSize: 14, color: THEME.muted, marginTop: 8, maxWidth: 400, margin: '8px auto 0' }}>
            Select filters above and click "Generate Report" to view detailed analytics
          </p>
        </div>
      )}
    </div>
  );
}
